import fromPairs from "lodash-es/fromPairs";

import * as bidders from "@/mk/ox/hbBidders";
import * as sizes from "@/mk/ox/hbSizes";
import { PrebidBidder } from "@/mk/ox/hbTypes";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

export enum Collapse {
    NEVER,
    AFTER_FETCH,
    BEFORE_FETCH,
}

export interface AdUnit {
    name: string;
    gptid: string;
    sizeConfig: sizes.AdUnitSizeConfig;
    bids: PrebidBidder[];
    collapse: Collapse;
    // Slots with autorefresh: true must also be declared in GAM under
    // Inventory > Inventory rules > Publisher declarations > Autorefresh
    autorefresh: boolean;
}

/**
 * AD UNIT NAMING CONVENTIONS
 *
 * We support TWO naming styles for backward compatibility:
 *
 * 1. NEW STYLE (Dash): " - Mobile" / " - Desktop"
 *    - Example: "Market Section - Mid 01 - Mobile"
 *    - Used by: Market Section, Market Item (Other 01/02)
 *    - Recommended for all new ad units
 *    - Explicitly includes device designation in zone name
 *
 * 2. OLD STYLE (Parentheses): " (Mobile)" / " (Desktop)"
 *    - Example: "Forum - Branding (Mobile)"
 *    - Used by: Forum, Groups, Blogs, Wiki, Homepage, Strollers, most Market Item units
 *    - Legacy naming, maintained for stability
 *    - Standard for existing ad units
 *
 * DYNAMIC SUFFIX HANDLING (AdSlotBranding component):
 *    - Some branding slots like "Market Item - Branding" get "(Mobile)" or "(Desktop)" appended dynamically
 *    - Implemented in getZoneWithDeviceSuffix() function in AdSlotBranding.tsx
 *    - Behavior:
 *      → If zone already has explicit suffix (any of the 4 styles) → used as-is
 *      → If not → old-style "(Mobile)" / "(Desktop)" suffix added for backward compatibility
 *    - This allows gradual migration without breaking existing pages
 *
 * SEARCHABILITY:
 *    - NEW STYLE zones can be searched directly (e.g., "Market Section - Mid 01 - Mobile")
 *    - OLD STYLE zones can be searched directly (e.g., "Forum - Branding (Mobile)")
 *    - DYNAMIC zones require searching for base name (e.g., "Market Item - Branding")
 *      and checking AdSlotBranding.tsx or the page's getBrandingProps() method
 */

/**
 * FORUM AD ZONES - PREFIX USAGE BY PAGE GROUP:
 *
 * "Forum Category - " prefix:
 *   - ForumCategoriesPage, ForumTopicsPage, ForumFeedPage, ForumMyActivityFeedPage
 *   - ListOfForumsPage, ArchivedCampaignsPage, CommonPlaceTopicsPage
 *   - FeedUntrustworthyUserPage, TopicPage, TopicInfoPage, FeedReportedPage
 *   → Used for: Branding, Halfpage, Anchor Leaderboard, Interstitial
 *
 * "Forum Other - " prefix:
 *   - PostLikersPage, PostCommentLikersPage, ForumSearchPage, CounsellingsHomePage
 *   → Used for: Branding, Halfpage, Interstitial (no Anchor Leaderboard)
 *
 * "Forum - " prefix:
 *   - PostDetailPage (page-level: Branding, Halfpage, Anchor Leaderboard, Interstitial)
 *   - PostDetail.tsx component (direct AdSlots: Banner, Mid 01, Rectangle 1/3/4/6, Halfpage 2)
 *
 * "Forum Thread - " prefix (legacy, still in use):
 *   - Rectangle 2, Rectangle 5 (not currently used in PostDetail.tsx, kept for compatibility)
 */

export const forum: AdUnit[] = [
    {
        name: "Forum - Top 01 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-branding2",
            202: "/10813505/mk.cz-branding2",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: {
            201: [bidders.adform.brandingDesktopSK],
            202: [bidders.adform.brandingDesktopCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum Category - Top 01 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forumcategory-top-1-d",
            202: "/10813505/mk.cz-forumcategory-top-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: {
            201: [bidders.adform.brandingDesktopSK],
            202: [bidders.adform.brandingDesktopCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum Category - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-forumcategory-top-1-m",
            202: "/10813505/mk.cz-forumcategory-top-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2WithFluid,
        bids: {
            201: [bidders.adform.brandingMobileSK],
            202: [bidders.adform.brandingMobileCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum Other - Branding (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-forumother-top-1-d",
            202: "/10813505/mk.cz-forumother-top-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: {
            201: [bidders.adform.brandingDesktopSK],
            202: [bidders.adform.brandingDesktopCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum Other - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-forumother-top-1-m",
            202: "/10813505/mk.cz-forumother-top-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingMobileOnly,
        bids: {
            201: [bidders.adform.brandingMobileSK],
            202: [bidders.adform.brandingMobileCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-banner1",
            202: "/10813505/mk.cz-banner1",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.bannerMobileOnly,
        bids: {
            201: [bidders.adform.brandingMobileSK],
            202: [bidders.adform.brandingMobileCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        // sticky zona musi byt oznacena v AdX, aby neporusovala podmienky (Inventory rules > Publisher declarations)
        name: "Forum - Mid 01 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-halfpage2",
            202: "/10813505/mk.cz-halfpage2",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Mid 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-halfpage9",
            202: "/10813505/mk.cz-halfpage9",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Mid 02 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle10_02",
            202: "/10813505/mk.cz-rectangle10_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Mid 03 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-halfpage12",
            202: "/10813505/mk.cz-halfpage12",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Mid 02 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forum-mid-02-d",
            202: "/10813505/mk.cz-forum-mid-02-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangle640x480DesktopOnly,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: false,
    },
    {
        name: "Forum - Mid 03 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forum-mid-03-d",
            202: "/10813505/mk.cz-forum-mid-03-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangle640x480DesktopOnly,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: false,
    },
    {
        name: "Forum - Mid 04 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forum-mid-04-d",
            202: "/10813505/mk.cz-forum-mid-04-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangle640x480DesktopOnly,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: false,
    },
    {
        name: "Forum - Mid 05 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forum-mid-05-d",
            202: "/10813505/mk.cz-forum-mid-05-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangle640x480DesktopOnly,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: false,
    },
    // {
    //     name: "Forum - Top 03 - Desktop",
    //     gptid: {
    //         201: "/10813505/mk.sk-rectangle7_02",
    //         202: "/10813505/mk.cz-rectangle7_02",
    //     }[ACTIVE_SERVER_ID],
    //     sizeConfig: sizes.rectangleDesktopOnly,
    //     // videoFormat: VideoFormat.OutstreamRectangle,
    //     bids: {
    //         201: [bidders.adform.rectangleSK],
    //         202: [bidders.adform.rectangleCZ],
    //     }[ACTIVE_SERVER_ID],
    //     collapse: Collapse.NEVER,
    //     autorefresh: true,
    // },
    {
        name: "Forum Thread - Rectangle 2 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-rectangle8_02",
            202: "/10813505/mk.cz-rectangle8_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Rectangle 3 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle9_02",
            202: "/10813505/mk.cz-rectangle9_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum Thread - Rectangle 5 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-rectangle28_02",
            202: "/10813505/mk.cz-rectangle28_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Mid 04 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle29_02",
            202: "/10813505/mk.cz-rectangle29_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum Category - Rectangle 1 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle14_02",
            202: "/10813505/mk.cz-rectangle14_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum Category - Rectangle 2 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-rectangle15_02",
            202: "/10813505/mk.cz-rectangle15_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        // sticky zona musi byt oznacena v AdX, aby neporusovala podmienky (Inventory rules > Publisher declarations)
        name: "Forum Category - Mid 01 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-halfpage5",
            202: "/10813505/mk.cz-halfpage5",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        // sticky zona musi byt oznacena v AdX, aby neporusovala podmienky (Inventory rules > Publisher declarations)
        name: "Forum Category - Mid 02 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forumcategory-mid-02-d",
            202: "/10813505/mk.cz-forumcategory-mid-02-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        // sticky zona musi byt oznacena v AdX, aby neporusovala podmienky (Inventory rules > Publisher declarations)
        name: "Forum Other - Halfpage Sticky (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-forumother-mid-1-d",
            202: "/10813505/mk.cz-forumother-mid-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Top 02 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-nativerectangle3",
            202: "/10813505/mk.cz-nativerectangle3",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Other 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-nativerectangle4",
            202: "/10813505/mk.cz-nativerectangle4",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Forum - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-anchorleaderboard1",
            202: "/10813505/mk.cz-anchorleaderboard1",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Forum Category - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-anchorleaderboard6",
            202: "/10813505/mk.cz-anchorleaderboard6",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
];

export const groups: AdUnit[] = [
    {
        name: "Groups - Branding (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-branding4",
            202: "/10813505/mk.cz-branding4",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: {
            201: [bidders.adform.brandingDesktopSK],
            202: [bidders.adform.brandingDesktopCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-branding11",
            202: "/10813505/mk.cz-branding11",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingWithRectangleMobileOnly,
        bids: {
            201: [bidders.adform.brandingMobileSK],
            202: [bidders.adform.brandingMobileCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Post - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-groups-post-top-01-m",
            202: "/10813505/mk.cz-groups-post-top-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Other - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-groups-other-top-01-m",
            202: "/10813505/mk.cz-groups-other-top-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Halfpage Sticky (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-halfpage3",
            202: "/10813505/mk.cz-halfpage3",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Mid 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-halfpage10",
            202: "/10813505/mk.cz-halfpage10",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Rectangle 1 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle20_02",
            202: "/10813505/mk.cz-rectangle20_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Rectangle 3 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle22_02",
            202: "/10813505/mk.cz-rectangle22_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Mid 02 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle23_02",
            202: "/10813505/mk.cz-rectangle23_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Mid 03 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle30_02",
            202: "/10813505/mk.cz-rectangle30_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Mid 04 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle31_02",
            202: "/10813505/mk.cz-rectangle31_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Groups Feed - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-anchorleaderboard7",
            202: "/10813505/mk.cz-anchorleaderboard7",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Groups Post - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-groups-post-anchor-01-m",
            202: "/10813505/mk.cz-groups-post-anchor-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Groups Other - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-groups-other-anchor-01-m",
            202: "/10813505/mk.cz-groups-other-anchor-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Groups Post - Mid 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle38_02",
            202: "/10813505/mk.cz-rectangle38_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
];

export const blogs: AdUnit[] = [
    {
        name: "Blogs - Branding (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-branding3",
            202: "/10813505/mk.cz-branding3",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: {
            201: [bidders.adform.brandingDesktopSK],
            202: [bidders.adform.brandingDesktopCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Feed - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-branding10",
            202: "/10813505/mk.cz-branding10",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: {
            201: [bidders.adform.brandingMobileSK],
            202: [bidders.adform.brandingMobileCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Article - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-blogs-article-top-01-m",
            202: "/10813505/mk.cz-blogs-article-top-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Post - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-blogs-post-top-01-m",
            202: "/10813505/mk.cz-blogs-post-top-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Feed - Halfpage Sticky (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-halfpage4",
            202: "/10813505/mk.cz-halfpage4",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Feed - Rectangle 1 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle16_02",
            202: "/10813505/mk.cz-rectangle16_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    // {
    //     name: "Blogs Feed - Rectangle 2 (Mobile)",
    //     gptid: {
    //         201: "/10813505/mk.sk-rectangle17_02",
    //         202: "/10813505/mk.cz-rectangle17_02",
    //     }[ACTIVE_SERVER_ID],
    //     sizeConfig: sizes.rectangleMobileOnly,
    //     bids: {
    //         201: [bidders.adform.rectangleSK],
    //         202: [bidders.adform.rectangleCZ],
    //     }[ACTIVE_SERVER_ID],
    //     collapse: Collapse.AFTER_FETCH,
    // },
    {
        name: "Blogs Feed - Rectangle 3 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle18_02",
            202: "/10813505/mk.cz-rectangle18_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Feed - Mid 02 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle19_02",
            202: "/10813505/mk.cz-rectangle19_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Feed - Mid 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-halfpage11",
            202: "/10813505/mk.cz-halfpage11",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        // sticky zona musi byt oznacena v AdX, aby neporusovala podmienky (Inventory rules > Publisher declarations)
        name: "Blogs Article - Halfpage Sticky (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-halfpage7",
            202: "/10813505/mk.cz-halfpage7",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Article - Rectangle 1 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle11_02",
            202: "/10813505/mk.cz-rectangle11_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Article - Rectangle 2 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle12_02",
            202: "/10813505/mk.cz-rectangle12_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Blogs Article - Mid 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle13_02",
            202: "/10813505/mk.cz-rectangle13_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    // {
    //     name: "Blogs Article - Rectangle 4 (Mobile)",
    //     gptid: {
    //         201: "/10813505/mk.sk-rectangle26_02",
    //         202: "/10813505/mk.cz-rectangle26_02",
    //     }[ACTIVE_SERVER_ID],
    //     sizeConfig: sizes.rectangleMobileOnly,
    //     bids: {
    //         201: [bidders.adform.rectangleSK],
    //         202: [bidders.adform.rectangleCZ],
    //     }[ACTIVE_SERVER_ID],
    //     collapse: Collapse.AFTER_FETCH,
    // },
    {
        name: "Blogs Feed - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-anchorleaderboard4",
            202: "/10813505/mk.cz-anchorleaderboard4",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Blogs Article - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-anchorleaderboard5",
            202: "/10813505/mk.cz-anchorleaderboard5",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Blogs Post - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-blogs-post-anchor-01-m",
            202: "/10813505/mk.cz-blogs-post-anchor-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Blogs Article - Mid 02 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle37_02",
            202: "/10813505/mk.cz-rectangle37_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
];

export const market: AdUnit[] = [
    {
        name: "Market Section - Branding (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-branding5",
            202: "/10813505/mk.cz-branding5",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Section - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-branding12",
            202: "/10813505/mk.cz-branding12",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.marketSectionWithHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Dashboard - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-marketdashboard-top-01-m",
            202: "/10813505/mk.cz-marketdashboard-top-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.marketSectionWithoutHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        // sticky zona musi byt oznacena v AdX, aby neporusovala podmienky (Inventory rules > Publisher declarations)
        name: "Market Item - Halfpage Sticky (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-halfpage1",
            202: "/10813505/mk.cz-halfpage1",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageOffsite,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        // CeMi: 14.6.2017 - vypnuty, generuje len 250Eur mesacne, a kazi nizkym CTR priame kampane
        //     name: 'Market Item - Billboard (Desktop)',
        //     gptid: {
        //         201: '/10813505/mk.sk-billboard1',
        //         202: '/10813505/mk.cz-billboard1',
        //     }[ACTIVE_SERVER_ID],
        //     sizeConfig: sizes.billboard,
        //     bids: {
        //         201: [bidders.adform.billboardSK],
        //         202: [bidders.adform.billboardCZ],
        //     }[ACTIVE_SERVER_ID],
        //     collapse: Collapse.AFTER_FETCH,
        // } {,
        name: "Market Item - Rectangle 1 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle4_02",
            202: "/10813505/mk.cz-rectangle4_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: [],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Market Item - Mid 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle5_02",
            202: "/10813505/mk.cz-rectangle5_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Item - Mid 02 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle6_02",
            202: "/10813505/mk.cz-rectangle6_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnlyV2,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Item - Branding (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-marketitem-top-d",
            202: "/10813505/mk.cz-marketitem-top-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Item - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-marketitem-top-m",
            202: "/10813505/mk.cz-marketitem-top-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnlyV2,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Item - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-marketitem-sticky-m",
            202: "/10813505/mk.cz-marketitem-sticky-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Market Item - Other 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-marketitem-other-01-m",
            202: "/10813505/mk.cz-marketitem-other-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Item - Other 02 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-marketitem-other-02-m",
            202: "/10813505/mk.cz-marketitem-other-02-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Section - Mid 01 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-billboard2",
            202: "/10813505/mk.cz-billboard2",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.billboard,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Section - Mid 02 - Desktop",
        gptid: {
            201: "/10813505/mk.sk-billboard3",
            202: "/10813505/mk.cz-billboard3",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.billboard,
        bids: [],
        collapse: Collapse.NEVER,
        // }, {
        //     name: 'Market Section - Rectangle 1 (Mobile)',
        //     gptid: {
        //         201: '/10813505/mk.sk-rectangle1_02',
        //         202: '/10813505/mk.cz-rectangle1_02',
        //     }[ACTIVE_SERVER_ID],
        //     sizeConfig: sizes.rectangleMobileOnly,
        //     bids: [],
        //     collapse: Collapse.AFTER_FETCH,
        autorefresh: true,
    },
    {
        name: "Market Section - Mid 02 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle2_02",
            202: "/10813505/mk.cz-rectangle2_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.marketSectionWithHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Section - Mid 03 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle3_02",
            202: "/10813505/mk.cz-rectangle3_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.marketSectionWithoutHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Section - Mid 04 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle32_02",
            202: "/10813505/mk.cz-rectangle32_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.marketSectionWithoutHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Section - Mid 05 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle33_02",
            202: "/10813505/mk.cz-rectangle33_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.marketSectionWithHalfpageMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Market Section - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-anchorleaderboard8",
            202: "/10813505/mk.cz-anchorleaderboard8",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Market Section - Mid 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-halfpage13",
            202: "/10813505/mk.cz-halfpage13",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.marketSectionWithHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
];

export const wiki: AdUnit[] = [
    {
        name: "Wiki - Branding (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-branding6",
            202: "/10813505/mk.cz-branding6",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: {
            201: [bidders.adform.brandingDesktopSK],
            202: [bidders.adform.brandingDesktopCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Wiki - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-branding13",
            202: "/10813505/mk.cz-branding13",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingMobileOnly,
        bids: {
            201: [bidders.adform.brandingMobileSK],
            202: [bidders.adform.brandingMobileCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        // sticky zona musi byt oznacena v AdX, aby neporusovala podmienky (Inventory rules > Publisher declarations)
        name: "Wiki - Halfpage Sticky (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-halfpage8",
            202: "/10813505/mk.cz-halfpage8",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Wiki - Rectangle 1 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle27_02",
            202: "/10813505/mk.cz-rectangle27_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Wiki - Rectangle 2 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-rectangle34_02",
            202: "/10813505/mk.cz-rectangle34_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Wiki - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-anchorleaderboard3",
            202: "/10813505/mk.cz-anchorleaderboard3",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyV2,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
];

export const homepage: AdUnit[] = [
    {
        name: "Homepage - Branding (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-branding1",
            202: "/10813505/mk.cz-branding1",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: {
            201: [bidders.adform.brandingDesktopSK],
            202: [bidders.adform.brandingDesktopCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER, // avoid CLS penalty for above-the-fold homepage slots
        autorefresh: true,
    },
    {
        name: "Homepage - Rectangle 1 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle24_02",
            202: "/10813505/mk.cz-rectangle24_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER, // avoid CLS penalty for above-the-fold homepage slots
        autorefresh: true,
    },
    {
        name: "Homepage - Other 03 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-halfpage14",
            202: "/10813505/mk.cz-halfpage14",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Homepage - Other 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-rectangle25_02",
            202: "/10813505/mk.cz-rectangle25_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: {
            201: [bidders.adform.rectangleSK],
            202: [bidders.adform.rectangleCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Homepage - NativeRectangle 1 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-nativerectangle1",
            202: "/10813505/mk.cz-nativerectangle1",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.nativeRectangleDesktopOnly,
        bids: [],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Other 02 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-nativerectangle2",
            202: "/10813505/mk.cz-nativerectangle2",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.nativeRectangleMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Homepage - Anchor - Mobile",
        gptid: {
            201: "/10813505/mk.sk-anchorleaderboard2",
            202: "/10813505/mk.cz-anchorleaderboard2",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.leaderboardMobileOnlyNoDownscale,
        bids: [],
        collapse: Collapse.BEFORE_FETCH,
        autorefresh: true,
    },
    {
        name: "Homepage - Top (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-homepage-top-m",
            202: "/10813505/mk.cz-homepage-top-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Top (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-homepage-top-d",
            202: "/10813505/mk.cz-homepage-top-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 01 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-01-m",
            202: "/10813505/mk.cz-homepage-mid-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 01 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-01-d",
            202: "/10813505/mk.cz-homepage-mid-01-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 02 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-02-m",
            202: "/10813505/mk.cz-homepage-mid-02-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 02 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-02-d",
            202: "/10813505/mk.cz-homepage-mid-02-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 03 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-03-m",
            202: "/10813505/mk.cz-homepage-mid-03-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 03 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-03-d",
            202: "/10813505/mk.cz-homepage-mid-03-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 04 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-04-m",
            202: "/10813505/mk.cz-homepage-mid-04-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 04 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-04-d",
            202: "/10813505/mk.cz-homepage-mid-04-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 05 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-05-m",
            202: "/10813505/mk.cz-homepage-mid-05-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 05 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-05-d",
            202: "/10813505/mk.cz-homepage-mid-05-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 06 (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-06-m",
            202: "/10813505/mk.cz-homepage-mid-06-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageMobileOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Mid 06 (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-homepage-mid-06-d",
            202: "/10813505/mk.cz-homepage-mid-06-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleOrHalfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.AFTER_FETCH,
        autorefresh: false,
    },
    {
        name: "Homepage - Halfpage Sticky (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-homepage-halfpage-sticky-d",
            202: "/10813505/mk.cz-homepage-halfpage-sticky-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
];

export const mail: AdUnit[] = [
    {
        name: "Mail - Commercial (Desktop & Mobile)",
        gptid: {
            201: "/10813505/mk.sk-ip1",
            202: "/10813505/mk.cz-ip1",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.mail,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Mail - CTA Button (Desktop & Mobile)",
        gptid: {
            201: "/10813505/mk.sk-ip3",
            202: "/10813505/mk.cz-ip3",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.mailCtaButton,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
];

export const strollers: AdUnit[] = [
    {
        name: "Strollers - Branding (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-branding17",
            202: "/10813505/mk.cz-branding17",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Strollers - Top 01 - Mobile",
        gptid: {
            201: "/10813505/mk.sk-branding18",
            202: "/10813505/mk.cz-branding18",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.brandingMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Strollers Index - Halfpage Sticky (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-halfpage6",
            202: "/10813505/mk.cz-halfpage6",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.halfpageDesktopOnly,
        bids: {
            201: [bidders.adform.halfpageSK],
            202: [bidders.adform.halfpageCZ],
        }[ACTIVE_SERVER_ID],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Strollers Detail - Rectangle (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-rectangle35_02",
            202: "/10813505/mk.cz-rectangle35_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
    {
        name: "Strollers Detail - Rectangle (Mobile)",
        gptid: {
            201: "/10813505/mk.sk-rectangle36_02",
            202: "/10813505/mk.cz-rectangle36_02",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.rectangleMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: true,
    },
];

export const interstitial: AdUnit[] = [
    {
        name: "Common - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-interstitial1",
            202: "/10813505/mk.cz-interstitial1",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Interstitial (Desktop)",
        gptid: {
            201: "/10813505/mk.sk-interstitial2",
            202: "/10813505/mk.cz-interstitial2",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
];

export const interstitialSectional: AdUnit[] = [
    {
        name: "Other - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-other-interstitial-1-m",
            202: "/10813505/mk.cz-other-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Other - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-other-interstitial-1-d",
            202: "/10813505/mk.cz-other-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Forum - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-forum-interstitial-1-m",
            202: "/10813505/mk.cz-forum-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Forum - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forum-interstitial-1-d",
            202: "/10813505/mk.cz-forum-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Forum Category - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-forumcategory-interstitial-1-m",
            202: "/10813505/mk.cz-forumcategory-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Forum Category - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forumcategory-interstitial-1-d",
            202: "/10813505/mk.cz-forumcategory-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Forum Other - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-forumother-interstitial-1-m",
            202: "/10813505/mk.cz-forumother-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Forum Other - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-forumother-interstitial-1-d",
            202: "/10813505/mk.cz-forumother-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Blogs Feed - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-blogs-interstitial-1-m",
            202: "/10813505/mk.cz-blogs-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Blogs Article - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-blogs-article-interstitial-01-m",
            202: "/10813505/mk.cz-blogs-article-interstitial-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Blogs Post - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-blogs-post-interstitial-01-m",
            202: "/10813505/mk.cz-blogs-post-interstitial-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Blogs - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-blogs-interstitial-1-d",
            202: "/10813505/mk.cz-blogs-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Groups - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-groups-interstitial-1-m",
            202: "/10813505/mk.cz-groups-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Groups Post - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-groups-post-interstitial-01-m",
            202: "/10813505/mk.cz-groups-post-interstitial-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Groups Other - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-groups-other-interstitial-01-m",
            202: "/10813505/mk.cz-groups-other-interstitial-01-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Groups - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-groups-interstitial-1-d",
            202: "/10813505/mk.cz-groups-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Market Item - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-marketitem-interstitial-1-m",
            202: "/10813505/mk.cz-marketitem-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Market Item - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-marketitem-interstitial-1-d",
            202: "/10813505/mk.cz-marketitem-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Market Section - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-marketsection-interstitial-1-m",
            202: "/10813505/mk.cz-marketsection-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Market Section - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-marketsection-interstitial-1-d",
            202: "/10813505/mk.cz-marketsection-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Strollers - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-strollers-interstitial-1-m",
            202: "/10813505/mk.cz-strollers-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Strollers - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-strollers-interstitial-1-d",
            202: "/10813505/mk.cz-strollers-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Wiki - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-wiki-interstitial-1-m",
            202: "/10813505/mk.cz-wiki-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Wiki - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-wiki-interstitial-1-d",
            202: "/10813505/mk.cz-wiki-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Homepage - Interstitial - Mobile",
        gptid: {
            201: "/10813505/mk.sk-homepage-interstitial-1-m",
            202: "/10813505/mk.cz-homepage-interstitial-1-m",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialMobileOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
    {
        name: "Homepage - Interstitial - Desktop",
        gptid: {
            201: "/10813505/mk.sk-homepage-interstitial-1-d",
            202: "/10813505/mk.cz-homepage-interstitial-1-d",
        }[ACTIVE_SERVER_ID],
        sizeConfig: sizes.interstitialDesktopOnly,
        bids: [],
        collapse: Collapse.NEVER,
        autorefresh: false,
    },
];

// see https://admanager.google.com/10813505#inventory/ad_unit/list
export const AD_UNITS: { [adUnitName: string]: AdUnit } = fromPairs(
    [
        ...homepage,
        ...forum,
        ...groups,
        ...blogs,
        ...market,
        ...wiki,
        ...mail,
        ...strollers,
        ...interstitial,
        ...interstitialSectional,
    ].map((adUnit) => [adUnit.name, adUnit]),
);

// Validate: non-anchor autorefresh slots must have collapse: NEVER
Object.values(AD_UNITS).forEach((adUnit) => {
    const isAnchor = adUnit.name.includes("Anchor");
    if (adUnit.autorefresh && !isAnchor && adUnit.collapse !== Collapse.NEVER) {
        console.error(
            `[hbAdUnits] Ad unit "${adUnit.name}" has autorefresh but collapse is not NEVER. ` +
                `Non-anchor autorefresh slots must use collapse: NEVER.`,
        );
    }
});

export function isBrandingDesktopAdUnit(adUnit: AdUnit) {
    return adUnit.name.endsWith("Branding (Desktop)");
}

export function isMailAdUnit(adUnit: AdUnit) {
    return (
        adUnit.name === "Mail - Commercial (Desktop & Mobile)" ||
        adUnit.name === "Mail - CTA Button (Desktop & Mobile)"
    );
}
